// StatPanelTemplate — infographic.
// 3-5 oversized numbers in a row, each with a small caption beneath.
// Reference: Apple "By the numbers" stat slides + Stripe metrics row.
//
// Per-stat layout: massive tabular number (text-display-2 or larger),
// 1px hairline divider between stats, eyebrow-style caption.

import { motion } from 'framer-motion';
import { Stage } from '../stage/Stage';
import { Orb } from '../stage/Orb';
import { Eyebrow } from '../stage/Eyebrow';
import { fadeUp, stagger } from '../motion/variants';
import type { SceneContent } from '../types';

interface StatPanelProps {
  content: SceneContent;
}

export function StatPanelTemplate({ content }: StatPanelProps) {
  const { headline, eyebrow, stats = [] } = content;
  const cells = stats.slice(0, 5);
  const colsClass =
    cells.length <= 2 ? 'grid-cols-2' :
    cells.length === 3 ? 'grid-cols-3' :
    cells.length === 4 ? 'grid-cols-4' :
    'grid-cols-5';

  return (
    <Stage>
      {/* Orb anchor — top-right, 48px */}
      <div className="absolute top-12 right-12 max-md:top-6 max-md:right-6">
        <Orb size={48} />
      </div>

      <motion.div
        initial="hidden"
        animate="show"
        exit="exit"
        variants={stagger(0.1)}
        className="m-auto w-full max-w-[1440px] flex flex-col gap-16"
      >
        {(eyebrow || headline) && (
          <div className="text-center flex flex-col gap-6 items-center">
            {eyebrow && (
              <motion.div variants={fadeUp}>
                <Eyebrow>{eyebrow}</Eyebrow>
              </motion.div>
            )}
            {headline && (
              <motion.h2
                variants={fadeUp}
                className="text-headline font-display max-w-[28ch] text-balance text-fg"
              >
                {headline}
              </motion.h2>
            )}
          </div>
        )}

        <motion.div
          variants={stagger(0.1)}
          className={`grid ${colsClass} gap-px bg-fg-faint/30 rounded-2xl overflow-hidden max-md:grid-cols-2`}
        >
          {cells.map((s, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              className="bg-void-800 px-8 py-12 flex flex-col items-center text-center gap-4 min-h-[220px] justify-center"
            >
              <span className="font-display tabular-nums tracking-[-0.04em] font-semibold text-fg text-[clamp(40px,5vw,80px)] leading-[0.95]">
                {s.value}
              </span>
              <span className="text-eyebrow text-fg-mute uppercase font-medium tracking-[0.18em] text-balance max-w-[16ch]">
                {s.label}
              </span>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </Stage>
  );
}
